import { ValueAdapter } from '../value.adapter';
import { format } from 'app/shared/utils/string.util';
import { IViolation, Owned, ParameterConfig, ParameterConfigUtils, Type } from 'shell-models-lib';
import { IKeyValPair } from 'app/interfaces/IKeyValPair';

export interface IParameterVal {
    value: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toParameterObject(pArray: ParameterConfig[] | undefined, hasType: boolean = false, toResourceFuc?: Function, stringLiteralTemplateValue?: boolean): any {
    if (pArray != null && pArray.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result: any = {};
        pArray.forEach((v) => {
            if (hasType) {
                result[v.name] = {
                    value: toResourceFuc ? toResourceFuc.call(v, v, ParameterConfig.VALUE) : ValueAdapter.toResource(v, ParameterConfig.VALUE, stringLiteralTemplateValue),
                    type: v.type
                };
            } else {
                result[v.name] = toResourceFuc ? toResourceFuc.call(v, v, ParameterConfig.VALUE) : ValueAdapter.toResource(v, ParameterConfig.VALUE, stringLiteralTemplateValue);
            }
        });
        return result;
    } else {
        return undefined;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toParameterArray(pArray: ParameterConfig[] | undefined): any {
    if (pArray != null && pArray.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resultArray: any = [];
        pArray.forEach((item) => {
            let value = item.value;
            if (item.type === Type.Expression) {
                value = ValueAdapter.toResource(item, ParameterConfig.VALUE)?.value || ValueAdapter.toResource(item, ParameterConfig.VALUE);
            } else if (item.type === Type.Object) {
                const valAdapterObj = ValueAdapter.toResource(item, ParameterConfig.VALUE);
                const valAdapterObjVal = ValueAdapter.toResource(item, ParameterConfig.VALUE)?.value;
                let isSet: boolean = false;
                try {
                    const parseObj = JSON.parse(valAdapterObj);
                    value = parseObj;
                    isSet = true;
                } catch {
                    value = item.value;
                }
                if (!isSet) {
                    try {
                        const parseObjVal = JSON.parse(valAdapterObjVal);
                        value = parseObjVal;
                    } catch {
                        // TODO: Discuss and clean up code as needed.
                        // JSON was not able to be parsed
                        // If set to valAdapterObjVal will allow incorrect JSON to go through - which the user can self-correct in JSON editor.
                        // If we remove || valAdapterObjVal, and let it be set to item.value - it may NOT display the value at all in JSON editor.
                        value = item.value || valAdapterObjVal;
                    }
                }
            } else if (item.type === Type.Array) {
                const valArray = value as string[]; // TODO: For now, assume all strings. Change as needed, probably to any[].
                value = valArray?.map((item) => {
                    return {
                        type: Type.String, // String type
                        content: item
                    };
                });
            }
            resultArray.push({
                key: item.name,
                value: {
                    type: item.type,
                    content: value
                }
            });
        });
        return resultArray;
    } else {
        return undefined;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toParameterClass(pObject: any, owner: Owned, hasType: boolean = false, isArray: boolean = false): ParameterConfig[] | undefined {
    if (pObject != null) {
        const result: ParameterConfig[] = [];
        if (isArray && hasType) { // pObject is array for Set Variable's pipeline return values
            for (const item of pObject) {
                const paramConfig = Owned.create<ParameterConfig>(ParameterConfig.MODEL, owner);
                paramConfig.name = item.key;
                const value = item.value;
                if (value) {
                    paramConfig.type = value.type;
                    if (value.type === Type.Object) {
                        try {
                            const objVal = JSON.stringify(value.content);
                            paramConfig.value = objVal;
                        } catch {
                            paramConfig.value = value.content;
                        }
                    } else if (value.type === Type.Array) {
                        // The p-chips component used in parameter-configure.component expects it in "flat" array.
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const valueArr = value.content?.map((item: { type: Type, content: any; }) => {
                            return item.content;
                        });
                        paramConfig.value = valueArr;
                    } else {
                        ValueAdapter.toModel(paramConfig, ParameterConfig.VALUE, value.content);
                    }
                }

                result.push(paramConfig);
            }
        } else {
            for (const propertyName in pObject) {
                const paramConfig = Owned.create<ParameterConfig>(ParameterConfig.MODEL, owner);
                paramConfig.name = propertyName;

                if (hasType) {
                    paramConfig.type = pObject[propertyName].type;
                    ValueAdapter.toModel(paramConfig, ParameterConfig.VALUE, pObject[propertyName].value);
                } else {
                    ValueAdapter.toModel(paramConfig, ParameterConfig.VALUE, pObject[propertyName]);
                }

                result.push(paramConfig);
            }
        }
        return result;
    } else {
        return [];
    }
}

// propertyName specifies UI model property name
export function createParameterConfigurationValidator(allowNull: boolean = false,
                                                      propertyName?: string): (_activity?: {}, configs?: ParameterConfig[], violation?: IViolation) => IViolation[] | undefined {
    return ParameterConfigUtils.createParameterConfigurationValidator(allowNull, propertyName);
}

// some parameter configs need extra validation rules depends on different config name, eg: cursorMethod.
export interface IParameterConfigExtraValidators {
    parameterName: string;
    parameterPattern: RegExp;
}

// provide parameter configs and some extra validators, return validating error message, currently only used in copy wizard.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getErrorMessageInParameterConfigs(configs: ParameterConfig[], isValueRequired: boolean, extraValidators?: IParameterConfigExtraValidators[]): string {
    if (configs != null) {
        let errorMessage;
        const nonEmptyConfigs = configs.filter((config) => config != null && config.name != null && config.name !== '');
        if (nonEmptyConfigs != null) {
            const nonEmptylen = nonEmptyConfigs.length;
            if (nonEmptylen < configs.length) {
                errorMessage = stringResources.Activity_Error_Configuration_InvalidName;
                return errorMessage;
            }
            const configSet = new Set(nonEmptyConfigs.map((item) => item.name));
            if (configSet != null && nonEmptyConfigs != null && configSet.size !== nonEmptylen) {
                errorMessage = stringResources.ParameterConfiguration_Error_DuplicateName;
                return errorMessage;
            }
        }
        if (isValueRequired) {
            configs.every((config) => {
                if (config.value == null || config.value === '') {
                    errorMessage = format(stringResources.Activity_Error_Configuration_InvalidValue, config.name ? config.name : '');
                    return false;
                }
                return true;
            });
            if (errorMessage != null) {
                return errorMessage;
            }
        }
        if (extraValidators != null && extraValidators.length > 0) {
            extraValidators.every((extraValidator) => {
                if (extraValidator != null && extraValidator.parameterName && extraValidator.parameterPattern) {
                    const parameterValue = configs.filter((config) => config != null && config.name != null && config.name === extraValidator.parameterName);
                    if (parameterValue && parameterValue.length === 1 && !extraValidator.parameterPattern.test(parameterValue[0].value)) {
                        errorMessage = extraValidator.parameterName + ': ' + format(stringResources.Validator_ErrorMessage_Pattern, extraValidator.parameterPattern.source);
                        return false;
                    }
                }
                return true;
            });
            if (errorMessage != null) {
                return errorMessage;
            }
        }
        return errorMessage;
    }
    return null;
}

export function parameterConfigToKeyValPair(configs: ParameterConfig[]): Array<IKeyValPair<IParameterVal>> {
    if (configs != null) {
        return configs.map((config) => (
            {
                key: config.name,
                val: {
                    value: config.value
                }
            }));
    }
    return undefined;
}

export function keyValPairToParameterConfig(configs: Array<IKeyValPair<IParameterVal>>): ParameterConfig[] {
    if (configs != null) {
        return configs.map((config) => {
            const paraConfig = new ParameterConfig();
            paraConfig.name = config.key;
            paraConfig.value = config.val.value;
            return paraConfig;
        });
    }
    return undefined;
}
